import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { X, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_invest-pro-170/artifacts/jqc1m5xr_Logo.png";

const SUGGESTIONS = [
  "Why is Nifty moving today?",
  "Latest RBI repo rate impact",
  "Most subscribed SME IPO this week?",
];

/**
 * Floating Ask AI toggle — Nivesh Street logo on a red circle,
 * visible across mobile / tablet / desktop.
 */
export default function AskAIWidget() {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const paneRef = useRef(null);

  useEffect(() => {
    if (open && paneRef.current) paneRef.current.scrollTop = paneRef.current.scrollHeight;
  }, [messages, open]);

  const ask = async (q) => {
    const query = (q ?? question).trim();
    if (query.length < 3) return toast.error("Ask something a bit longer");
    setMessages(m => [...m, { role: "user", text: query }]);
    setQuestion("");
    setBusy(true);
    try {
      const r = await api.post("/askai/ask", { question: query });
      setMessages(m => [...m, { role: "ai", text: r.data.answer, model: r.data.model }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "AI is busy. Try again.";
      setMessages(m => [...m, { role: "ai", text: `⚠️ ${detail}`, error: true }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{ position: "fixed" }}
          className="z-[60] bottom-5 right-5 sm:bottom-6 sm:right-6 bg-[#D91E2A] hover:bg-[#B01820] text-white shadow-2xl rounded-full h-14 w-14 flex items-center justify-center transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#D91E2A]/30 border-2 border-white"
          data-testid="askai-fab"
          aria-label="Open Ask AI"
          title="Ask AI · Nivesh Street research desk"
        >
          <span className="font-heading font-extrabold text-base tracking-wide text-white">Ask</span>
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
        </button>
      )}

      {open && (
        <div className="fixed z-50 bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 max-h-[75vh] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="askai-widget">
          <div className="flex items-center justify-between px-4 py-3 bg-[#D91E2A] text-white">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center overflow-hidden p-1">
                <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
              </div>
              <span className="font-heading font-bold text-sm">Ask AI</span>
              <span className="text-[9px] font-mono-fin uppercase tracking-widest text-white/70">Nivesh Desk</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10" data-testid="askai-close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={paneRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50 min-h-[240px]">
            {messages.length === 0 && (
              <div className="text-center py-6 px-2">
                <Sparkles className="w-6 h-6 text-[#D91E2A] mx-auto mb-2" />
                <p className="text-sm text-slate-700 font-semibold mb-1">Hi — I'm your market research desk.</p>
                <p className="text-xs text-slate-500 mb-4">Ask anything about Indian equities, IPOs, macro, or crypto.</p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => ask(s)} className="w-full text-left text-xs px-3 py-2 rounded-md bg-white border border-slate-200 hover:border-[#D91E2A]/40 text-slate-700 transition-colors" data-testid={`askai-suggest-${s.slice(0, 8)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#D91E2A] text-white" : m.error ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-white text-slate-800 border border-slate-200"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Researching…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 p-2 border-t border-slate-200 bg-white">
            <input
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="Type your question…"
              maxLength={500}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-[#D91E2A]"
              data-testid="askai-widget-input"
            />
            <button type="submit" disabled={busy || question.trim().length < 3} className="p-2 rounded-md bg-[#D91E2A] hover:bg-[#B01820] disabled:opacity-40 text-white" data-testid="askai-widget-send" aria-label="Send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
